/**
 * Cabecera móvil THCC (sections/thcc-cabecera.liquid, snippets/thcc-nav-menu-movil.liquid).
 *
 * 1. Menú: la hamburguesa abre el <dialog> modal `#thcc-mm` (foco atrapado y Esc los da el navegador).
 *    Se cierra con la X, tocando el fondo, al pasar a 990 px o más y al navegar.
 * 2. Buscador: la lupa despliega/pliega `#thcc-busq-movil` (Figma 84:2); Esc lo pliega y devuelve el foco.
 * El menú rige por debajo de 990 px; el buscador desplegable, por debajo de 750 px.
 */

const MOVIL = window.matchMedia('(max-width: 989px)');

/* ---------- 1. Menú ---------- */

function initMenu() {
  const dialog = document.getElementById('thcc-mm');
  if (!(dialog instanceof HTMLDialogElement)) return;

  // Abridores: la hamburguesa de la cabecera (tableta) y el botón central de la barra inferior (móvil).
  // Delegación en document para no depender de qué se haya pintado antes de cargar este módulo.
  const openers = () => /** @type {HTMLElement[]} */ ([...document.querySelectorAll('[data-thcc-mm-abrir]')]);
  /** @type {HTMLElement | null} */
  let lastOpener = null;

  /** @param {boolean} value */
  const setExpanded = (value) => {
    for (const el of openers()) el.setAttribute('aria-expanded', String(value));
  };

  const SIN_MOVIMIENTO = window.matchMedia('(prefers-reduced-motion: reduce)');
  /** @type {Animation[]} */
  let animaciones = [];
  let cerrando = false;

  // Burbuja: un círculo blanco que crece desde el botón que abrió el menú. Se anima solo con `transform`
  // (lo compone la GPU), mucho más fluido en teléfonos que animar clip-path.
  const burbuja = document.createElement('span');
  burbuja.className = 'thcc-mm__burbuja';
  burbuja.setAttribute('aria-hidden', 'true');
  dialog.prepend(burbuja);
  const contenidos = () => /** @type {HTMLElement[]} */ ([...dialog.children].filter((el) => el !== burbuja));

  const colocarBurbuja = () => {
    const r = (lastOpener ?? openers()[0])?.getBoundingClientRect();
    const x = r ? r.left + r.width / 2 : window.innerWidth / 2;
    const y = r ? r.top + r.height / 2 : window.innerHeight;
    const radio = Math.ceil(Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y)));
    burbuja.style.width = burbuja.style.height = `${radio * 2}px`;
    burbuja.style.left = `${x - radio}px`;
    burbuja.style.top = `${y - radio}px`;
    return 22 / radio; // escala inicial: del tamaño del botón
  };

  /** @param {boolean} abrir */
  const animar = (abrir) => {
    for (const a of animaciones) a.cancel();
    animaciones = [];
    if (SIN_MOVIMIENTO.matches || typeof burbuja.animate !== 'function') return Promise.resolve();
    const inicio = colocarBurbuja();
    dialog.classList.add('is-animando');
    const ms = abrir ? 460 : 340;
    const curva = abrir ? 'cubic-bezier(0.2, 0.8, 0.2, 1)' : 'cubic-bezier(0.5, 0, 0.75, 0)';
    const escalas = [`scale(${inicio})`, 'scale(1)'];
    animaciones.push(
      burbuja.animate({ transform: abrir ? escalas : escalas.reverse() }, { duration: ms, easing: curva, fill: 'both' })
    );
    for (const el of contenidos()) {
      animaciones.push(
        el.animate(
          abrir
            ? [{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'none' }]
            : [{ opacity: 1 }, { opacity: 0 }],
          { duration: abrir ? 300 : 140, delay: abrir ? 150 : 0, easing: 'ease-out', fill: 'both' }
        )
      );
    }
    try {
      animaciones.push(
        dialog.animate({ opacity: abrir ? [0, 1] : [1, 0] }, { duration: ms, easing: 'ease', fill: 'both', pseudoElement: '::backdrop' })
      );
    } catch {}
    return animaciones[0].finished.then(
      () => {
        if (abrir) dialog.classList.remove('is-animando');
      },
      () => {}
    );
  };

  const abrirMenu = () => {
    cerrando = false;
    dialog.showModal();
    setExpanded(true);
    animar(true);
  };

  /** Cierra con la animación inversa: el círculo regresa al botón que lo abrió. */
  const cerrarMenu = async () => {
    if (!dialog.open || cerrando) return;
    cerrando = true;
    await animar(false);
    cerrando = false;
    if (dialog.open) dialog.close();
    for (const a of animaciones) a.cancel();
    animaciones = [];
    dialog.classList.remove('is-animando');
  };

  document.addEventListener('click', (event) => {
    const el = /** @type {Element} */ (event.target).closest?.('[data-thcc-mm-abrir]');
    if (!(el instanceof HTMLElement) || dialog.open) return;
    lastOpener = el;
    abrirMenu();
  });

  // Esc: el navegador cerraría de golpe; se cancela y se cierra con animación.
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    cerrarMenu();
  });

  for (const el of dialog.querySelectorAll('[data-thcc-mm-cerrar]')) {
    el.addEventListener('click', () => cerrarMenu());
  }

  // Tocar el fondo (el propio <dialog>, fuera del panel) cierra.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) cerrarMenu();
  });

  dialog.addEventListener('close', () => {
    setExpanded(false);
    (lastOpener ?? openers()[0])?.focus({ preventScroll: true });
  });

  // Navegar desde un enlace del panel lo cierra (el panel puede sobrevivir en el historial).
  dialog.addEventListener('click', (event) => {
    const target = /** @type {Element} */ (event.target);
    const link = target.closest?.('a[href]');
    if (link && !link.hasAttribute('aria-disabled')) dialog.close();
  });

  MOVIL.addEventListener('change', (event) => {
    if (!event.matches && dialog.open) dialog.close();
  });

  window.addEventListener('pagehide', () => {
    if (dialog.open) dialog.close();
  });
}

/* ---------- 2. Buscador desplegable ---------- */

function initBusqueda() {
  const panel = document.querySelector('[data-thcc-busq-movil]');
  const trigger = document.querySelector('[data-thcc-busq-abrir]');
  if (!(panel instanceof HTMLElement) || !(trigger instanceof HTMLElement)) return;

  /** @param {boolean} open @param {boolean} [focus] */
  const set = (open, focus = true) => {
    panel.hidden = !open;
    trigger.setAttribute('aria-expanded', String(open));
    const input = /** @type {HTMLInputElement | null} */ (panel.querySelector('input[type="search"]'));
    if (open && focus) input?.focus({ preventScroll: true });
  };

  trigger.addEventListener('click', () => set(panel.hidden));

  panel.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    set(false);
    trigger.focus({ preventScroll: true });
  });

  MOVIL.addEventListener('change', (event) => {
    if (!event.matches) set(false, false);
  });
}

initMenu();
initBusqueda();
